import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import api from '@/lib/api';

/**
 * RecurringDigestBanner — surfaces recurring outgoing patterns detected in the
 * last 90 days of bank statements (powered by /accounting/reconciliation/
 * recurring-patterns) so the Reconciliation Center hygiene work doesn't get
 * forgotten between months.
 *
 * Hidden until the API returns ≥ 1 outgoing pattern that isn't already a
 * recurring entry. User dismissal persists in localStorage for 7 days.
 */
const DISMISS_KEY = 'recurring-digest-dismissed-until';

function fmt(v) {
  return (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export const RecurringDigestBanner = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Honor 7-day dismissal
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedUntil && dismissedUntil > Date.now()) {
      setHidden(true);
      return;
    }
    api.get('/accounting/reconciliation/recurring-patterns', { params: { days: 90, min_occurrences: 2 } })
      .then(r => setData(r.data))
      .catch(() => { /* silent fail */ });
  }, []);

  if (hidden || !data) return null;

  // Only show outgoing + non-recurring patterns
  const candidates = (data.patterns || []).filter(p => p.sign === 'out' && !p.already_recurring);
  if (candidates.length === 0) return null;

  // Aggregate monthly burden — convert each cadence to its monthly equivalent
  const cadenceFactor = { weekly: 4.33, 'bi-weekly': 2.17, monthly: 1, 'bi-monthly': 0.5, quarterly: 0.33, irregular: 0.5 };
  const monthlyBurden = candidates.reduce((s, p) => s + (p.amount || 0) * (cadenceFactor[p.cadence] || 0.5), 0);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setHidden(true);
  };

  const goToCenter = () => navigate('/reconciliation-center');

  return (
    <Card className="border-violet-200 bg-gradient-to-r from-violet-50/70 via-fuchsia-50/50 to-pink-50/40 shadow-sm" data-testid="recurring-digest-banner">
      <CardContent className="p-3.5 flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shrink-0 shadow-sm">
          <Sparkles size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-semibold text-violet-900">
            {candidates.length} recurring expense{candidates.length === 1 ? '' : 's'} not yet set up
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Worth ~<strong>{cur()} {fmt(monthlyBurden)}/month</strong> · spotted in your last 90 days of bank statements. Set them up once and your books reconcile themselves.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            onClick={goToCenter}
            className="h-8 text-xs gap-1 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white"
            data-testid="recurring-digest-cta"
          >
            Fix in 30s <ArrowRight size={12} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            data-testid="recurring-digest-dismiss"
            title="Hide for 7 days"
          >
            <X size={14} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecurringDigestBanner;
